<template>
	<div class="prebuild-series" v-show="isReady">
		<hero-section :series-info="seriesInfo" :prebuilds="seriesPrebuilds" />
		<prebuild-features :features-data="getFeaturesData" />
		<prebuild-specs :prebuilds="seriesPrebuilds" />
		<prebuild-bld-features :bld-features="getBldFeatures" />
		<prebuild-reviews
			:current-prebuild="getPrebuild"
			:reviews="reviewsByPrebuild"
			:infinite-scroll-handler="setReviewsByPage"
		/>

		<sticky-banner-slide
			v-if="isReady"
			trigger-element="#prebuild-fps"
			:offset="-80"
		>
			<prebuild-sticky-banner
				:title="bannerTitle"
				:description="bannerDescription"
			/>
		</sticky-banner-slide>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import _isEmpty from "lodash/isEmpty";
import HeroSection from "../hero-section";
import PrebuildSpecs from "../prebuild-specs";
import PrebuildFeatures from "../prebuild-features";
import PrebuildBldFeatures from "../prebuild-bld-features";
import PrebuildStickyBanner from "../prebuild-banners/prebuild-sticky-banner";
import StickyBannerSlide from "@c/generals/sticky-banner-slide.vue";
import PrebuildReviews from "../prebuild-reviews";
import { gtmTrack } from "@/util/gtm";

export default {
	name: "PrebuidSeries",
	beforeRouteEnter(to, from, next) {
		const {
			params: { hasSelectedGames },
			query: { type: routeSlug }
		} = to;
		next(vm => {
			vm.routeInitSeries({ slug: routeSlug, hasSelectedGames });
		});
	},
	provide() {
		return {
			currentPrebuild: {
				getPrebuild: () => this.getPrebuild,
				getVariant: () => this.getVariant,
				setPrebuild: this.setCurrentPrebuild,
				setVariantId: this.setCurrentVariantId,
				getReviews: () => this.reviewsByPrebuild
			},
			gamesPerformance: {
				getGames: () => this.getDefaultGames,
				resolution: () => this.resolution,
				setResolution: this.setResolution
			}
		};
	},
	components: {
		HeroSection,
		PrebuildSpecs,
		PrebuildFeatures,
		PrebuildBldFeatures,
		PrebuildStickyBanner,
		StickyBannerSlide,
		PrebuildReviews
	},
	data() {
		return {
			isReady: true,
			reviews: {
				page: 1,
				limit: 10,
				order: "created_at|desc"
			}
		};
	},
	computed: {
		...mapState({
			hasBeenInitialized: state => {
				return (
					!_isEmpty(state.PrebuildSeries.series_info) &&
					!_isEmpty(state.PrebuildSeries.prebuilds)
				);
			},
			resolution: state => state.StarterPc.resolution
		}),
		...mapGetters({
			seriesInfo: "PrebuildSeries/getSeriesInfo",
			seriesPrebuilds: "PrebuildSeries/getSeriesPrebuilds",
			prebuildsReviews: "PrebuildSeries/getPrebuildsReviews",
			currentPrebuildId: "PrebuildSeries/getCurrentPrebuildId",
			currentVariantId: "PrebuildSeries/getCurrentVariantId"
		}),
		getPrebuild() {
			if (
				!_isEmpty(this.seriesPrebuilds) &&
				!_isEmpty(this.currentPrebuildId)
			) {
				return this.seriesPrebuilds.find(
					prebuild => prebuild.id === this.currentPrebuildId
				);
			}
			return {};
		},
		getVariant() {
			if (!_isEmpty(this.getPrebuild) && !_isEmpty(this.currentVariantId)) {
				let prebuildVariant = this.getPrebuild.variants.find(
					variant => variant.id === this.currentVariantId
				);

				if (typeof prebuildVariant === "undefined") {
					prebuildVariant = this.getPrebuild.variants[0];
				}

				return prebuildVariant;
			}
			return {};
		},
		reviewsByPrebuild() {
			if (!_isEmpty(this.prebuildsReviews) && !_isEmpty(this.getPrebuild)) {
				return this.prebuildsReviews[this.getPrebuild.slug];
			}
			return {};
		},
		getDefaultGames() {
			if (!_isEmpty(this.getPrebuild)) {
				return this.getPrebuild.games;
			}
			return [];
		},
		getFeaturesData() {
			if (!_isEmpty(this.seriesInfo)) {
				return this.seriesInfo.sections.slice(1, 6);
			}
			return [];
		},
		getBldFeatures() {
			if (!_isEmpty(this.seriesInfo)) {
				return this.seriesInfo.sections.slice(6);
			}
			return [];
		},
		bannerTitle() {
			return _isEmpty(this.seriesInfo) ? "" : this.seriesInfo.title;
		},
		bannerDescription() {
			return _isEmpty(this.seriesInfo) ? "" : this.seriesInfo.description;
		}
	},
	watch: {
		getPrebuild(prebuild) {
			if (prebuild.name) {
				gtmTrack.prebuildVisited(prebuild.name);
			}
		}
	},
	methods: {
		...mapActions({
			fetchSeriesInfo: "PrebuildSeries/fetchSeriesInfo",
			fetchSeriesPrebuilds: "PrebuildSeries/fetchSeriesPrebuilds",
			fetchPrebuildsReviews: "PrebuildSeries/fetchPrebuildsReviews",
			setReviewsByPagination: "PrebuildSeries/setReviewsByPagination",
			setPrebuildSeries: "PrebuildSeries/setPrebuildSeries",
			setPrebuildId: "PrebuildSeries/setPrebuildId",
			setVariantId: "PrebuildSeries/setVariantId",
			setResolution: "StarterPc/setResolution",
			setAppLoader: "Application/setShowLoading"
		}),
		async routeInitSeries({ slug, hasSelectedGames }) {
			if (!this.hasBeenInitialized || hasSelectedGames) {
				await this.fetchStarterSeries();
			}

			const prebuild = this.initGetPrebuild({ slug });
			const variantId = this.getVariantIdOnInit({
				variants: prebuild.variants,
				prebuildId: prebuild.id
			});

			this.setCurrentPrebuildId(prebuild.id);
			this.setCurrentVariantId(variantId);
		},
		async fetchStarterSeries() {
			this.setIsReady(false);

			const seriesInfo = await this.fetchSeriesInfo({
				name: "starter-line"
			});
			const prebuilds = await this.fetchSeriesPrebuilds({ seriesInfo });
			const reviews = await this.fetchPrebuildsReviews({
				prebuilds,
				page: this.reviews.page,
				limit: this.reviews.limit,
				order: this.reviews.order
			});
			this.setPrebuildSeries({ seriesInfo, prebuilds, reviews });

			this.setIsReady(true);
		},
		// TO-DO: Create a strategy for the prebuild ID based on the slug or id
		initGetPrebuild({ slug }) {
			const id = this.getStoredPrebuildId();
			const hasSlug = slug !== undefined;
			const hasStoredPrebuildId = id !== null;

			if (hasSlug) {
				return this.getPrebuildBySlug(slug);
			}
			if (hasStoredPrebuildId) {
				return this.getPrebuildById(id);
			}
			return this.getDefaultPrebuild();
		},
		getPrebuildById(id) {
			return this.seriesPrebuilds.find(prebuild => prebuild.id === id);
		},
		getPrebuildBySlug(slug) {
			return this.seriesPrebuilds.find(prebuild => prebuild.slug === slug);
		},
		getDefaultPrebuild() {
			return this.seriesPrebuilds[0];
		},
		// TO-DO: Create a strategy for the variant ID based on the route scenario
		getVariantIdOnInit({ variants, prebuildId }) {
			const storedPrebuildId = this.getStoredPrebuildId();
			const isPrebuildInStorage = prebuildId === storedPrebuildId;
			let id = "";

			if (isPrebuildInStorage) {
				const storedId = localStorage.getItem("starter_variant_id");
				id = storedId !== null ? storedId : id;
			} else {
				const storedColorCode = localStorage.getItem(
					"stater_variant_color_code"
				);
				const variant = this.getCurrentVariant(variants, storedColorCode);
				id = variant.id;
			}

			return id;
		},
		setCurrentPrebuildId(prebuildId) {
			localStorage.setItem("starter_prebuild_id", prebuildId);
			this.setPrebuildId(prebuildId);
		},
		setCurrentPrebuild(payload) {
			const { id, variants } = payload;
			const [currentColorCode] = this.getVariant.color_codes;
			const variant = this.getCurrentVariant(variants, currentColorCode);

			this.setCurrentPrebuildId(id);
			this.setCurrentVariantId(variant.id);
		},
		getCurrentVariant(variants, colorCode) {
			const newVariantHasColor = variants.some(variant => variant.color_codes.includes(colorCode)
			);

			if (newVariantHasColor) {
				return this.getVariantByColor(variants, colorCode);
			}

			return this.getDefaultVariant(variants);
		},
		getVariantByColor(variants, colorCode) {
			return variants.find(variant => variant.color_codes.includes(colorCode));
		},
		getDefaultVariant(variants) {
			return variants[0];
		},
		getStoredPrebuildId() {
			return localStorage.getItem("starter_prebuild_id");
		},
		setCurrentVariantId(id) {
			localStorage.setItem("starter_variant_id", id);
			this.setVariantId(id);
			this.storeVariantColorCode();
		},
		storeVariantColorCode() {
			const [currentColorCode] = this.getVariant.color_codes;
			localStorage.setItem("stater_variant_color_code", currentColorCode);
		},
		setIsReady(payload) {
			const setLoader = !payload;
			this.setAppLoader(setLoader);

			this.isReady = payload;
		},
		async setReviewsByPage(page) {
			const { slug } = this.getPrebuild;
			await this.setReviewsByPagination({
				slug,
				page,
				limit: this.reviews.limit,
				order: this.reviews.order
			});
		}
	}
};
</script>

<style lang="scss" scoped>
.prebuild-series {
	font-family: "Akkurat-Pro";
	overflow-x: hidden;
}
</style>
